import type { Consent } from "./consent";
import { posthogEvent, type Moment } from "./moments";
import { maskAttribute, maskReplayRequest, scrubEvent, type OutgoingEvent } from "./posthog-privacy";
import { MASKED_TEXT_SELECTOR, REPLAY_ATTRIBUTE, type Screen } from "./screens";
import type { Sink } from "./sink";

/**
 * PostHog: product analytics and session replay, the same project the phone
 * app reports to, so one person is one person on both.
 *
 * Nothing loads until consent allows it: the SDK is a separate chunk fetched
 * on first use. Replay records clicks, scrolls and screen changes everywhere
 * except the sign-in callback, but words appear only on the screens
 * `screens.ts` marks open, and form fields never appear at all.
 */
export interface PostHogClient {
  init(token: string, config: Record<string, unknown>): unknown;
  capture(event: string, properties?: Record<string, unknown>): unknown;
  identify(distinctId: string): void;
  reset(): void;
  opt_in_capturing(): void;
  opt_out_capturing(): void;
  startSessionRecording(): void;
  stopSessionRecording(): void;
  set_config(config: Record<string, unknown>): void;
}

export interface PostHogOptions {
  readonly token: string;
  /** Our same-origin proxy, so ad blockers do not drop the data. */
  readonly apiHost: string;
  readonly origin: string;
  readonly doc: Document;
  readonly load: () => Promise<PostHogClient>;
  /** The visitor's stored choice, if they have made one. */
  readonly consent: Consent | null;
  /** Whether this visitor must say yes before anything is measured. */
  readonly waitsForConsent: boolean;
  readonly warn?: (message: string, error?: unknown) => void;
}

/** PostHog's own app, for links from the toolbar; data never goes here. */
const UI_HOST = "https://us.posthog.com";
/** Moments waiting for the SDK chunk to arrive. Past this, the oldest go. */
const MAX_PENDING = 50;

export function postHogConfig(options: Pick<PostHogOptions, "apiHost" | "origin">): Record<string, unknown> {
  return {
    api_host: options.apiHost,
    ui_host: UI_HOST,
    person_profiles: "identified_only",
    // Page views are ours, named by route pattern, sent when the screen changes.
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    rageclick: true,
    // Words and attributes start hidden; an open screen reveals them.
    mask_all_text: true,
    mask_all_element_attributes: true,
    // Replay starts when the first screen says it may.
    disable_session_recording: true,
    // Neither the console nor network traffic is ever part of a replay, and a
    // project setting in PostHog cannot turn them on.
    enable_recording_console_log: false,
    capture_performance: false,
    capture_exceptions: false,
    disable_surveys: true,
    disable_conversations: true,
    disable_product_tours: true,
    disable_external_dependency_loading: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: MASKED_TEXT_SELECTOR,
      maskAttributeFn: maskAttribute,
      recordHeaders: false,
      recordBody: false,
      maskCapturedNetworkRequestFn: (request: Record<string, unknown>) => maskReplayRequest(request, options.origin),
    },
    before_send: (event: OutgoingEvent | null) => scrubEvent(event, options.origin),
  };
}

export function createPostHogSink(options: PostHogOptions): Sink {
  const warn = options.warn ?? ((message, error) => console.warn(message, error));
  let allowed = options.consent === "granted" || (options.consent === null && !options.waitsForConsent);
  let client: PostHogClient | null = null;
  let loading = false;
  let pending: Moment[] = [];
  let userId: string | null = null;
  let current: Screen | null = null;
  let recording = false;

  function applyScreen(ph: PostHogClient, screen: Screen, pageview: boolean): void {
    const open = screen.policy === "open";
    ph.set_config({ mask_all_text: !open, mask_all_element_attributes: !open });
    if (screen.policy === "off" && recording) {
      ph.stopSessionRecording();
      recording = false;
    } else if (screen.policy !== "off" && !recording) {
      ph.startSessionRecording();
      recording = true;
    }
    if (pageview) ph.capture("$pageview", { screen: screen.route });
  }

  function send(ph: PostHogClient, moment: Moment): void {
    const [event, properties] = posthogEvent(moment);
    ph.capture(event, properties);
  }

  function start(): void {
    if (!allowed) return;
    if (client) {
      client.opt_in_capturing();
      if (userId) client.identify(userId);
      if (current) applyScreen(client, current, true);
      return;
    }
    if (loading) return;
    loading = true;
    options
      .load()
      .then((ph) => {
        ph.init(options.token, postHogConfig(options));
        client = ph;
        if (!allowed) {
          ph.opt_out_capturing();
          return;
        }
        if (userId) ph.identify(userId);
        if (current) applyScreen(ph, current, true);
        for (const moment of pending) send(ph, moment);
      })
      .catch((error: unknown) => warn("PostHog could not load; this visit is not measured.", error))
      .finally(() => {
        loading = false;
        pending = [];
      });
  }

  start();

  return {
    track(moment) {
      if (!allowed) return;
      if (client) send(client, moment);
      else pending = [...pending, moment].slice(-MAX_PENDING);
    },
    setConsent(consent) {
      allowed = consent === "granted";
      if (allowed) {
        start();
        return;
      }
      pending = [];
      if (client) {
        if (recording) client.stopSessionRecording();
        recording = false;
        client.opt_out_capturing();
      }
    },
    identify(id) {
      if (userId === id) return;
      userId = id;
      if (client && allowed) client.identify(id);
    },
    forget() {
      if (userId === null) return;
      userId = null;
      if (!client) return;
      client.reset();
      // A reset clears PostHog's own record of the choice; ours still stands.
      if (!allowed) client.opt_out_capturing();
    },
    screen(screen) {
      current = screen;
      // Set on every screen change, measured or not, so masking is decided
      // before the recorder could ever see the page.
      options.doc.documentElement.setAttribute(REPLAY_ATTRIBUTE, screen.policy);
      if (client && allowed) applyScreen(client, screen, true);
    },
  };
}
